import React from 'react';
import { BarChart, Trash2, Layers, ExternalLink } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для сравнения сохраненных сценариев инфляции
 */
const CompareScenariosWidget = ({ 
  savedScenarios, 
  deleteScenario, 
  loadScenario 
}) => {
  // Если нет сохраненных сценариев
  if (!savedScenarios || savedScenarios.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-red-500" />
          Сравнение сценариев
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные сценарии для сравнения результатов расчета инфляции.
        </p>
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
          <p className="text-xs text-blue-700 dark:text-blue-300">
            Рассчитайте инфляцию и нажмите "Сохранить сценарий", чтобы сохранить результаты для последующего сравнения.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-red-500" />
          Сравнение сценариев
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedScenarios.length} {savedScenarios.length === 1 ? 'сценарий' : savedScenarios.length < 5 ? 'сценария' : 'сценариев'}
        </span>
      </div>

      <div className="space-y-4">
        {savedScenarios.map((scenario) => {
          // Определяем тип расчета
          const calculationType = scenario.results.calculationType;
          const isAppreciation = calculationType === 'appreciation';
          const changeColor = isAppreciation ? 'emerald' : 'rose';
          
          return (
            <div 
              key={scenario.id} 
              className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            >
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  {scenario.name}
                </h4>
                <div className="flex items-center space-x-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Layers className="w-4 h-4" />}
                    onClick={() => loadScenario(scenario.id)}
                    title="Загрузить параметры"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Trash2 className="w-4 h-4 text-red-500" />}
                    onClick={() => deleteScenario(scenario.id)}
                    title="Удалить"
                  />
                </div>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <div className="flex justify-between mb-1">
                  <span>Начальная сумма:</span>
                  <span className="font-medium">
                    {formatCurrency(scenario.results.initialAmount)}
                  </span>
                </div>
                <div className="flex justify-between mb-1">
                  <span>Период:</span>
                  <span>
                    {scenario.results.startYear} — {scenario.results.endYear}
                  </span>
                </div>
                <div className="flex justify-between mb-1">
                  <span>Накопленная инфляция:</span>
                  <span className="font-medium text-red-600 dark:text-red-400">
                    {formatNumber(scenario.results.cumulativeInflation, 1)}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>{isAppreciation ? 'Конечная стоимость:' : 'Покупательная способность:'}</span>
                  <span className={`font-semibold text-${changeColor}-600 dark:text-${changeColor}-400`}>
                    {formatCurrency(scenario.results.finalValue)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {savedScenarios.length >= 2 && (
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
          <div className="flex items-start">
            <ExternalLink className="h-4 w-4 text-blue-500 mt-0.5 mr-2" />
            <p className="text-xs text-blue-700 dark:text-blue-300">
              Сохраненные сценарии позволяют сравнить влияние инфляции в разные периоды или для разных сумм.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompareScenariosWidget;